import React, { useState, useEffect } from "react";
import { Form, Modal, Button, Row, Image } from "react-bootstrap";
import ResponsiveTable from "../Grid/ResponsiveTable";
import InputBox from "../common/InputBox";
import SearchSelect from "../common/SearchSelect";
import editIcon from "../../assets/icons/EditIcon.svg";
import deleteIcon from "../../assets/icons/deleteA.svg";
import Switch from "../common/Switch";
import xclose from "../../assets/icons/x-close.svg";
import Alert from "../common/Alert";
import { getIsobPgStandardColorsData } from "../../services/isob-service";

const COMPONENT_HEADERS = [
    { columnName: "productGroup", displayName: "Product Group", displayIndication: true, dataType: "text", width: 300 },
    { columnName: "pgComponent", displayName: "PG Component", displayIndication: true, dataType: "text", width: 300 },
    { columnName: "color", displayName: "Color", displayIndication: true, dataType: "text", width: 200 },
    {
        columnName: "apoStd", displayName: "APO/Std", displayIndication: true, dataType: "text", width: 200, switch: true,
        cssFormatter: (value, header, item, rowIndex) => (
            <Switch isOn={!!item.apoStd} readOnly={true} index={`table_apoStd_${rowIndex}`} />
        )
    },
];

const DRAWING_HEADERS = [
    { columnName: "drawingNumber", displayName: "Drawing number", displayIndication: true, dataType: "text", width: 300 },
    { columnName: "revisionNumber", displayName: "Revision Number", displayIndication: true, dataType: "text", width: 300 },
];

export default function IsobTab2({ formValues, handleInputChange, mandatoryFields, setMandatoryFields, readOnly,error, setError,orderBookingFormData, getDropDownOptions }) {
    console.log("formValues.scopeType:: ", formValues.scopeType)
    const [components, setComponents] = useState(formValues.components || []);
    const [drawings, setDrawings] = useState(formValues.drawings || []);
    const [scopeType, setScopeType] = useState(formValues.scopeType || "");
    const [scopeText, setScopeText] = useState(formValues.scopeText || "");
    const [partOrder, setPartOrder] = useState(formValues.partOrder || "");

    useEffect(() => {
        setComponents(formValues.components || []);
        setDrawings(formValues.drawings || []);
        setScopeType(formValues.scopeType || "");
        setScopeText(formValues.scopeText || "");
        setPartOrder(formValues.partOrder || "");
    }, [formValues.components, formValues.drawings, formValues.scopeType, formValues.scopeText, formValues.partOrder, readOnly]);

    useEffect(() => {
        if (readOnly) {
            setMandatoryFields((prev) => prev.filter((f) => !["components", "drawings"].includes(f)));
            return;
        }
        const missing = [];
        if (!components || components.length === 0) missing.push("components");
        if (!drawings || drawings.length === 0) missing.push("drawings");
        setMandatoryFields((prev) => {
            const filtered = prev.filter((f) => !["components", "drawings"].includes(f));
            return [...filtered, ...missing];
        });
    }, [components, drawings, readOnly]);

    // Component Modal
    const [showCompModal, setShowCompModal] = useState(false);
    const [compForm, setCompForm] = useState({ productGroup: "", pgComponent: "", color: "", apoStd: false });
    const [editCompIdx, setEditCompIdx] = useState(null);
    const [masterData, setMasterData] = useState([]);
    const [productGroupOptions, setProductGroupOptions] = useState([]);
    const [pgComponentOptions, setPgComponentOptions] = useState([]);

    // Drawing Modal
    const [showDrawModal, setShowDrawModal] = useState(false);
    const [drawForm, setDrawForm] = useState({ drawingNumber: "", revisionNumber: "" });
    const [editDrawIdx, setEditDrawIdx] = useState(null);

    const syncComponents = (updated) => {
        setComponents(updated);
        handleInputChange({ target: { name: "components", value: updated } });
    };

    const syncDrawings = (updated) => {
        setDrawings(updated);
        handleInputChange({ target: { name: "drawings", value: updated } });
    };

    const isScopeEntire    = scopeType.includes("entire drawing is part of this order");
    const isScopeAdditional = scopeType.includes("Additional scope");
    const isScopePortion    = scopeType.includes("portion of the drawing");

    const scopeTextDisabled  = readOnly || isScopeEntire || isScopePortion;
    const partOrderDisabled  = readOnly || isScopeEntire || isScopeAdditional;
    const scopeTextRequired  = !readOnly && isScopeAdditional;
    const partOrderRequired  = !readOnly && isScopePortion;

    const handleScopeType = (e) => {
        const val = e.target.value;
        setScopeType(val);
        handleInputChange(e);
        const newIsEntire     = val.includes("entire drawing is part of this order");
        const newIsAdditional = val.includes("Additional scope");
        const newIsPortion    = val.includes("portion of the drawing");
        if (newIsEntire || newIsPortion) {
            setScopeText("");
            handleInputChange({ target: { name: "scopeText", value: "" } });
            setError((prev) => ({ ...prev, scopeText: "" }));
            setMandatoryFields((prev) => prev.filter((f) => f !== "scopeText"));
        }
        if (newIsEntire || newIsAdditional) {
            setPartOrder("");
            handleInputChange({ target: { name: "partOrder", value: "" } });
            setError((prev) => ({ ...prev, partOrder: "" }));
            setMandatoryFields((prev) => prev.filter((f) => f !== "partOrder"));
        }
        if (newIsAdditional) setMandatoryFields((prev) => prev.includes("scopeText") ? prev : [...prev, "scopeText"]);
        if (newIsPortion)    setMandatoryFields((prev) => prev.includes("partOrder") ? prev : [...prev, "partOrder"]);
    };

    const handleScopeText = (e) => {
        const val = e.target.value.slice(0, 1000);
        setScopeText(val);
        handleInputChange({ target: { name: "scopeText", value: val } });
    };

    const handlePartOrder = (e) => {
        const val = e.target.value.slice(0, 1000);
        setPartOrder(val);
        handleInputChange({ target: { name: "partOrder", value: val } });
    };

    // Component handlers
    const handleAddComponent = () => {
        if (components.length >= 10) {
            Alert('error', 'Cannot add more than 10 components');
            return;
        }
        setCompForm({ productGroup: "", pgComponent: "", color: "", apoStd: false });
        setPgComponentOptions([]);
        setEditCompIdx(null);
        setShowCompModal(true);
    };

    const handleEditComponent = async (item) => {
        const idx = components.findIndex((c) => c === item);
        setCompForm({ ...item });
        setEditCompIdx(idx);
        if (item.productGroup) await fetchPgComponentOptions(item.productGroup);
        setShowCompModal(true);
    };

    const handleDeleteComponent = (item) => {
        syncComponents(components.filter((c) => c !== item));
        Alert('success', 'Component details deleted successfully.');
    };

    const handleSaveComponent = () => {
        if (editCompIdx !== null) {
            const updated = components.map((c, i) => i === editCompIdx ? { ...compForm } : c);
            syncComponents(updated);
            Alert('success', 'Component details updated successfully.');
        } else {
            if (components.length >= 10) return;
            syncComponents([...components, { ...compForm }]);
            Alert('success', 'Component details added successfully.');
        }
        setShowCompModal(false);
        setError((prev) => ({ ...prev, productGroup: "", pgComponent: "" }));
    };
    const handleAddDrawing = () => {
        if (drawings.length >= 10) {
            Alert('error', 'Cannot add more than 10 drawing details');
            return;
        }
        setDrawForm({ drawingNumber: "", revisionNumber: "" });
        setEditDrawIdx(null);
        setShowDrawModal(true);
    };

    const handleEditDrawing = (item) => {
        const idx = drawings.findIndex((d) => d === item);
        setDrawForm({ ...item });
        setEditDrawIdx(idx);
        setShowDrawModal(true);
    };

    const handleDeleteDrawing = (item) => {
        syncDrawings(drawings.filter((d) => d !== item));
        Alert('success', 'Drawing details deleted successfully.');
    };

    const handleSaveDrawing = () => {
        if (editDrawIdx !== null) {
            const updated = drawings.map((d, i) => i === editDrawIdx ? { ...drawForm } : d);
            syncDrawings(updated);
            Alert('success', 'Drawing details updated successfully.');
        } else {
            if (drawings.length >= 10) return;
            syncDrawings([...drawings, { ...drawForm }]);
            Alert('success', 'Drawing details added successfully.');
        }
        setShowDrawModal(false);
        setError((prev) => ({ ...prev, drawingNumber: "", revisionNumber: "" }));
    };


    const fetchData = async () => {
        try {
            // debugger
            const response = await getIsobPgStandardColorsData();
            console.log("master data:: ", response.data);
            setMasterData(response.data);
            const uniqueTitles = [...new Set(response.data.map(item => item.Title))];
            console.log("unique titles:: ", uniqueTitles)
            setProductGroupOptions(uniqueTitles);

            console.log("product group values:: ", productGroupOptions)
        } catch (error) {
            console.error("error in catch", error);
            setMasterData([]);
            setProductGroupOptions([]);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);


    const fetchPgComponentOptions = async (selectedPg) => {
        try {
            const filteredComponents = masterData
                .filter(item => item.Title.toLowerCase() === selectedPg.toLowerCase())
                .map(item => item.Component);
            const uniqueComponents = [...new Set(filteredComponents)];
            setPgComponentOptions(uniqueComponents);
        } catch (error) {
            console.error("Error fetching PG Component options", error);
            setPgComponentOptions([]);
        }
    };

    const pgComponentOptionData = pgComponentOptions.map((item) => (
        <option key={item} value={item}>
            {item}
        </option>
    ));

    const closeComp = async (e) => {
        e.preventDefault();
        setShowDrawModal(false);
        setShowCompModal(false);
        setError((prev) => ({ ...prev, productGroup: "", pgComponent: "" }));
    };

    const closeDraw = async (e) => {
        e.preventDefault();
        setShowDrawModal(false);
        setShowCompModal(false);
        setError((prev) => ({ ...prev, drawingNumber: "", revisionNumber: "" }));
    };

    const componentIcons = readOnly ? [] : [
        { src: editIcon, click: handleEditComponent },
        { src: deleteIcon, click: handleDeleteComponent },
    ];

    const drawingIcons = readOnly ? [] : [
        { src: editIcon, click: handleEditDrawing },
        { src: deleteIcon, click: handleDeleteDrawing },
    ];

    return (
        <div className="form-right-adjust">
            {/* Component Details */}
            <div className="ordorder-info-section-plain-verticalpad add-edit-form isob-table">
                <div className="isob-tab2-table-header">
                    <h6 className="section-subheading isob-tab2-table-heading">Component Details</h6>
                    {!readOnly && (
                        <span className="isob-tab2-add-link" onClick={handleAddComponent}>
                            &#43; Add New Component
                        </span>
                    )}
                </div>
                <ResponsiveTable
                    sessionName="Component Details"
                    headers={COMPONENT_HEADERS}
                    data={components}
                    icons={componentIcons}
                    handleAdd={handleAddComponent}
                    dataType="componentDetails"
                    noCheckbox={true}
                    paginationView="false"
                    DisplayExcelDownload={false}
                    searchmd={6}
                    noSearch={true}
                    noScroll={true}
                    noEmptyState={true}
                    noHeader={true}
                />
            </div>
            <br />
            {/* Drawing Details */}
            <div className="ordorder-info-section-plain-verticalpad add-edit-form isob-table">
                <div className="isob-tab2-table-header">
                    <h6 className="section-subheading isob-tab2-table-heading">Drawing Details</h6>
                    {!readOnly && (
                        <span className="isob-tab2-add-link" onClick={handleAddDrawing}>
                            &#43; Add Drawing details
                        </span>
                    )}
                </div>
                <ResponsiveTable
                    sessionName="Drawing Details"
                    headers={DRAWING_HEADERS}
                    data={drawings}
                    icons={drawingIcons}
                    handleAdd={handleAddDrawing}
                    dataType="drawingDetails"
                    noCheckbox={true}
                    paginationView="false"
                    DisplayExcelDownload={false}
                    searchmd={6}
                    noSearch={true}
                    noScroll={true}
                    noEmptyState={true}
                    noHeader={true}
                />
            </div>

            {/* Scope Details */}
            <div className="add-edit-form scope-details">
                <h6 className="section-subheading">Scope Details</h6>
                <div className="order-info-section-colored-norightmar add-edit-form nomargin drawing-scope">
                    <SearchSelect 
                        name="scopeType"
                        id="scopeType"
                        option={getDropDownOptions("Drawing Scope")}
                        label="Specify if entire scope is part of the supply"
                        value={scopeType}
                        onChange={(e) => handleScopeType(e)}
                        fieldName="Specify if entire scope is part of the supply"
                        setError={setError}
                        error={error?.scopeType}
                        readOnly={readOnly}
                        // required={!readOnly}
                        // mandatoryFields={mandatoryFields}
                        // setMandatoryFields={setMandatoryFields}
                    />

                    <InputBox
                        type="text"
                        style={{ "max-width": '98%', "min-width": '50%', "width": '-webkit-fill-available', "min-height": "30px" }}
                        name="scopeText"
                        id="scopetext"
                        label={`Specify additional scope to be considered${scopeTextRequired ? " *" : ""}`}
                        value={scopeText}
                        onChange={handleScopeText}
                        fieldName="Specify additional scope to be considered"
                        error={error?.scopeText}
                        setError={setError}
                        readOnly={scopeTextDisabled}
                        required={scopeTextRequired}
                        mandatoryFields={mandatoryFields}
                        setMandatoryFields={setMandatoryFields}
                        isAlphanumeric
                        maxLength={200}
                    />
                    <InputBox
                        type="text"
                        style={{ "max-width": '98%', "min-width": '50%', "width": '-webkit-fill-available', "min-height": "30px" }}
                        name="partOrder"
                        id="partorder"
                        label={`Explain what is part of the order and what is not${partOrderRequired ? " *" : ""}`}
                        value={partOrder}
                        onChange={handlePartOrder}
                        fieldName="Explain what is part of the order and what is not"
                        error={error?.partOrder}
                        setError={setError}
                        readOnly={partOrderDisabled}
                        required={partOrderRequired}
                        mandatoryFields={mandatoryFields}
                        setMandatoryFields={setMandatoryFields}
                        isAlphanumeric
                        maxLength={200}
                    />
                    {/* <Form.Group className="scope-details mb-2" style={{ marginTop: "16px", width: "100%" }}>
                        <Form.Label style={{ fontSize: "13px", color: "#6F6F6F" }}>*Specify additional scope to be considered</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={scopeText}
                            onChange={handleScopeText}
                            maxLength={1000}
                        />
                        <div style={{ textAlign: "right", fontSize: "12px", color: "#999" }}>{scopeText.length} / 1000 chars</div>
                    </Form.Group> */}
                </div>
            </div>

            {/* Add/Edit Component Modal */}
            <Modal show={showCompModal} centered>
                <Modal.Header>
                    <Modal.Title>{editCompIdx !== null ? "Edit" : "Add"} Component</Modal.Title>
                    <Image src={xclose} onClick={closeComp} ></Image>
                </Modal.Header>
                <Modal.Body>
                    <Row className="modal-row add-edit-form pg-comp-row">
                        <SearchSelect
                            label="Product Group *"
                            name="productGroup"
                            id="modal_productGroup"
                            value={compForm.productGroup}
                            onChange={(e) => {
                                setCompForm({ ...compForm, productGroup: e.target.value, pgComponent: "", color: "" });
                                if (e.target.value) fetchPgComponentOptions(e.target.value);
                                else setPgComponentOptions([]);
                            }}
                            required={true}
                            fieldName="Product Group"
                            setError={setError}
                            error={error?.productGroup}
                            mandatoryFields={[]}
                            setMandatoryFields={() => { }}
                            option={(orderBookingFormData?.PG?.results || orderBookingFormData?.PG || []).map((pg, idx) => (
                                <option key={idx} value={pg}>{pg}</option>
                            ))}
                        />
                        <SearchSelect
                            label="PG Component *"
                            name="pgComponent"
                            id="modal_pgComponent"
                            value={compForm.pgComponent}
                            onChange={(e) => {
                                const selectedComponent = e.target.value;
                                const matchingItem = masterData.find((item) =>
                                    item.Title === compForm.productGroup && item.Component === selectedComponent
                                );
                                setCompForm({
                                    ...compForm,
                                    pgComponent: selectedComponent,
                                    color: matchingItem?.StandardColor || ""
                                });
                            }}
                            required={true}
                            fieldName="PG Component"
                            setError={setError}
                            error={error?.pgComponent}
                            mandatoryFields={[]}
                            setMandatoryFields={() => { }}
                            option={pgComponentOptionData}
                        />
                    </Row>
                    <Row className="modal-row add-edit-form pg-comp-field pg-comp-row">
                        <InputBox
                            label="Color"
                            name="color"
                            id="modal_color"
                            type="text"
                            value={compForm.color}
                            onChange={(e) => setCompForm({ ...compForm, color: e.target.value })}
                            fieldName="Color"
                            readOnly={!compForm.apoStd}
                            isAlphanumeric
                            setError={() => { }}
                            mandatoryFields={[]}
                            setMandatoryFields={() => { }}
                        />
                        <div className="switch-div isob-tab2-apo-switch">
                            <Form.Label className="isob-tab2-apo-label">APO Color</Form.Label>
                            <Switch
                                isOn={
                                    compForm.apoStd == true ? true : false
                                }
                                handleToggle={(e) => {
                                    const isApo = e.target.checked;
                                    if (!isApo) {
                                        const matchingItem = masterData.find((item) =>
                                            item.Title === compForm.productGroup && item.Component === compForm.pgComponent
                                        );
                                        setCompForm({ ...compForm, apoStd: false, color: matchingItem?.StandardColor || "" });
                                    } else {
                                        setCompForm({ ...compForm, apoStd: true });
                                    }
                                }}
                                index="modal_apoStd"
                            ></Switch>

                        </div>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsPrefix="reset-btn" className="reset-btn-primary" onClick={() => { setShowCompModal(false); setCompForm({ productGroup: "", pgComponent: "", color: "", apoStd: false }); setError((prev) => ({ ...prev, productGroup: "", pgComponent: "" })); }}>Cancel</Button>
                    <Button variant="primary" className="btn-primary" onClick={handleSaveComponent} disabled={!compForm.productGroup || !compForm.pgComponent}>
                        {editCompIdx !== null ? "Update" : "Add"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add/Edit Drawing Modal */}
            <Modal show={showDrawModal} centered>
                <Modal.Header>
                    <Modal.Title>{editDrawIdx !== null ? "Edit" : "Add"} Drawing</Modal.Title>
                    <Image src={xclose} onClick={closeDraw} ></Image>
                </Modal.Header>
                <Modal.Body>
                    <Row className="modal-row add-edit-form">
                        <InputBox
                            label="Drawing Number *"
                            name="drawingNumber"
                            id="modal_drawingNumber"
                            type="text"
                            value={drawForm.drawingNumber}
                            onChange={(e) => setDrawForm({ ...drawForm, drawingNumber: e.target.value })}
                            required={true}
                            fieldName="Drawing Number"
                            isAlphanumericWithDashSlash
                            // setError={() => { }}
                            setError={setError}
                            error={error?.drawingNumber}
                            mandatoryFields={[]}
                            setMandatoryFields={() => { }}
                            maxLength={20}
                        />
                        <InputBox
                            label="Revision Number *"
                            name="revisionNumber"
                            id="modal_revisionNumber"
                            type="text"
                            value={drawForm.revisionNumber}
                            onChange={(e) => setDrawForm({ ...drawForm, revisionNumber: e.target.value })}
                            required={true}
                            fieldName="Revision Number"
                            isAlphanumericWithDashSlash
                            // setError={() => { }}
                            setError={setError}
                            error={error?.revisionNumber}
                            mandatoryFields={[]}
                            setMandatoryFields={() => { }}
                            maxLength={20}
                        />
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsPrefix="reset-btn" className="reset-btn-primary" onClick={() => { setShowDrawModal(false); setError((prev) => ({ ...prev, drawingNumber: "", revisionNumber: "" })); }}>Cancel</Button>
                    <Button variant="primary" className="btn-primary" onClick={handleSaveDrawing} disabled={!drawForm.drawingNumber || !drawForm.revisionNumber || (error.drawingNumber && error.drawingNumber != "") || (error.revisionNumber && error.revisionNumber != "")}>
                        {editDrawIdx !== null ? "Update" : "Add"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
